import React, { useEffect, useRef } from 'react';

/**
 * Premium background — orb glows, grid lines & canvas grain.
 * Adapted from core-ai-apresentacao.html with project blue palette.
 *
 * When reducedMotion is true the orbs pause and grain fades out via CSS
 * transitions so the switch is smooth instead of a jarring DOM swap.
 */
export const AnimatedBackground = React.memo(({ reducedMotion = false }: { reducedMotion?: boolean }) => {
    const grainRef = useRef<HTMLDivElement>(null);
    // Respect system "Reduce motion" — also disables the grain canvas (~30KB
    // PNG kept in memory) which contributes to long-running tab heaviness.
    const systemReducedMotion = typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const effectiveReducedMotion = reducedMotion || systemReducedMotion;

    useEffect(() => {
        if (effectiveReducedMotion) return; // skip grain entirely
        if (!grainRef.current) return;
        const canvas = document.createElement('canvas');
        canvas.width = 150;
        canvas.height = 150;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const imageData = ctx.createImageData(150, 150);
        for (let i = 0; i < imageData.data.length; i += 4) {
            const v = Math.random() * 255;
            imageData.data[i] = v;
            imageData.data[i + 1] = v;
            imageData.data[i + 2] = v;
            imageData.data[i + 3] = 255;
        }
        ctx.putImageData(imageData, 0, 0);
        grainRef.current.style.backgroundImage = `url(${canvas.toDataURL('image/png')})`;
    }, [effectiveReducedMotion]);

    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 bg-[#040410]">
            {/* Orb glows — fade out smoothly when reducedMotion */}
            <div
                className="absolute inset-0 animated-orbs"
                style={{
                    opacity: effectiveReducedMotion ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                    animationPlayState: effectiveReducedMotion ? 'paused' : 'running',
                }}
            />

            {/* Canvas grain — disabled entirely when reducedMotion (no PNG kept in memory) */}
            {!effectiveReducedMotion && (
                <div
                    ref={grainRef}
                    className="absolute inset-0"
                    style={{
                        opacity: 0.03,
                        transition: 'opacity 0.3s ease',
                        backgroundRepeat: 'repeat',
                        backgroundSize: '150px 150px',
                    }}
                />
            )}

            <style>{`
                .animated-orbs {
                    background:
                        radial-gradient(ellipse 700px 700px at 85% 10%, rgba(29, 78, 255, 0.09) 0%, transparent 70%),
                        radial-gradient(ellipse 550px 550px at 10% 85%, rgba(59, 130, 246, 0.07) 0%, transparent 70%),
                        radial-gradient(ellipse 450px 450px at 50% 50%, rgba(100, 60, 255, 0.05) 0%, transparent 70%);
                    animation: orbs-shift 20s ease-in-out infinite alternate;
                    will-change: background-position;
                }

                @keyframes orbs-shift {
                    0% { background-position: 0% 0%, 0% 0%, 0% 0%; }
                    100% { background-position: 5% 8%, -5% -6%, 3% -4%; }
                }

                /* Respeita configuração de sistema "reduzir movimento" — pausa
                   orbs animation pra alívio em CPU/GPU em PCs mais antigos */
                @media (prefers-reduced-motion: reduce) {
                    .animated-orbs { animation: none !important; }
                }
            `}</style>
        </div>
    );
});
